import React, {useEffect, useRef, useCallback} from 'react';
import clsx from 'clsx';
import _ from 'underscore';

import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	dragResizeClasses,
	getDragResizeUtilityClass,
	DragResizeClasses,
	DRAG_RESIZE_CLASS_NAME,
} from './dragResizeClasses';
import {DragResizeDirection} from './constants';
import {dragResizeHandleClasses} from './dragResizeHandleClasses';
import {DragResizeHandle} from './DragResizeHandle';
import {useDragResize} from './useDragResize';

type Ref = React.LegacyRef<HTMLDivElement>;

type DragResizeUtiltiyClassProps = WithClasses<
	DragResizeClasses,
	{
		dragging?: boolean;
		direction: DragResizeDirection;
	}
>;

const useUtilityClasses = (ownerState: DragResizeUtiltiyClassProps) => {
	const {classes, direction, dragging} = ownerState;
	const slots = {
		root: ['root', direction, dragging && 'dragging'],
		content: ['content'],
		sentinel: ['sentinel', direction],
	};
	return composeClasses(slots, getDragResizeUtilityClass, classes);
};

const DragResizeRoot = styled('div', {
	name: DRAG_RESIZE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {dragging, direction} = ownerState as DragResizeUtiltiyClassProps;
	const horizontal = [
		DragResizeDirection.LEFT,
		DragResizeDirection.RIGHT,
	].includes(direction);
	return {
		position: 'relative',
		width: 0,
		minWidth: 0,
		willChange: horizontal
			? 'width, max-width, min-width'
			: 'height, max-height, min-height',
		[`&:not(:hover):not(.${dragResizeClasses.dragging}) .${dragResizeHandleClasses.root}`]:
			{
				opacity: 0,
			},
		...(dragging
			? {
					[`& .${dragResizeClasses.content}`]: {
						pointerEvents: 'none',
					},
			  }
			: {
					transition: horizontal
						? theme.transitions.create(['width', 'max-width', 'min-width'])
						: theme.transitions.create(['height', 'max-height', 'min-height']),
			  }),
	};
});

const DragResizeContent = styled('div', {
	name: DRAG_RESIZE_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	overflow: 'hidden',
	width: '100%',
}));

const DragResizeSentinel = styled('div')(({theme}) => ({
	position: 'absolute',
	pointerEvents: 'none',
	[`&.${dragResizeClasses.bottom}`]: {
		right: 0,
		bottom: 0,
		left: 0,
		height: 1,
	},
	[`&.${dragResizeClasses.left}`]: {
		right: 0,
		top: 0,
		bottom: 0,
		width: 1,
	},
	[`&.${dragResizeClasses.right}`]: {
		left: 0,
		top: 0,
		bottom: 0,
		width: 1,
	},
	[`&.${dragResizeClasses.top}`]: {
		right: 0,
		top: 0,
		left: 0,
		height: 1,
	},
}));

const SIDES = {
	[DragResizeDirection.LEFT]: 'right',
	[DragResizeDirection.RIGHT]: 'left',
	[DragResizeDirection.TOP]: 'bottom',
	[DragResizeDirection.BOTTOM]: 'top',
} as const;

export type DragResizeProps = WithClasses<
	DragResizeClasses,
	React.PropsWithChildren<
		Parameters<typeof useDragResize>[0] & {
			ref?: Ref;
		}
	>
>;

const DragResizeInner = (
	{classes: _classes, children, className, ...props}: DragResizeProps,
	ref: Ref,
) => {
	const {isDragging, checkLimits, direction, handleMouseDown, styles} =
		useDragResize(props);

	const ownerState: DragResizeUtiltiyClassProps = {
		classes: _classes,
		dragging: isDragging,
		direction,
	};

	const classes = useUtilityClasses(ownerState);

	const handleObserver = useCallback(() => checkLimits(), []);

	const observer = useRef<IntersectionObserver>(
		new IntersectionObserver(handleObserver, {
			threshold: 1,
			root: props.overflowParentEl?.current,
		}),
	);

	useEffect(() => {
		return () => {
			observer.current.disconnect();
		};
	}, []);

	const registerRef = useCallback((node: any) => {
		if (!node) return;
		observer.current?.observe(node);
	}, []);

	const side = SIDES[direction];

	return (
		<DragResizeRoot
			className={clsx(classes.root, className)}
			style={styles}
			ref={ref}
			ownerState={ownerState}
		>
			<DragResizeContent className={classes.content}>
				{children}
			</DragResizeContent>
			<DragResizeSentinel
				className={classes.sentinel}
				ref={registerRef}
				data-id={side}
			/>
			{!props.disabled ? (
				<DragResizeHandle
					direction={direction}
					isDragging={isDragging}
					onMouseDown={handleMouseDown}
				/>
			) : null}
		</DragResizeRoot>
	);
};

export const DragResizePrimitive = React.forwardRef(DragResizeInner) as (
	props: DragResizeProps,
) => JSX.Element;

export const DragResize = DragResizePrimitive;

export default DragResize;
